import { includes, map } from 'lodash';
import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { FormProvider, SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button, Label, Navbar } from 'reactstrap';
import { DeepPartial } from 'tsdef/dist/node';
import { gql, useMutation } from 'urql';
import { useId } from '../../hooks';
import styled from '../../themes/themed-styled-components';
import { DeepPartialGraph, Override } from '../../types';
import { DangerousGoodsInput, HacpackMutationCreateOrUpdateHandlingsArguments, Handling, HandlingInput, HandlingsResult, HandlingType } from '../../types/graph';
import { TerminalContext } from '../TerminalRoot';
import { GridLayout, HorizontalScroll } from '../ui';
import { DangerousGoodsFieldValue, DangerousGoodsFormTable, dangerousGoodsToFieldValueWithKey } from './DangerousGoodsFormTable';

const Form = styled.form`
height: 100%;
`

const Wrap = styled.div`
height: 100%;
overflow: auto;
& > div:first-child {
    padding: 1em;
    position: sticky;
    left: 0;
    overflow-x: auto;
}
`

//type PathTo<TFieldValues extends FieldValues = FieldValues, TFieldType = any, TFieldArrayName extends ArrayPath<TFieldValues> = ArrayPath<TFieldValues>> = PathValue<TFieldValues, TFieldArrayName> extends TFieldType ? TFieldArrayName : never;

export type Props = {
    // @ts-ignore
    handling?: DeepPartialGraph<Handling>,
    editableFields?: Array<keyof Handling>,
    // @ts-ignore
    onSaved?: (handling: DeepPartialGraph<Handling>) => void,
    onCancel?: () => void
}

export type HandlingFormValue = Override<Partial<HandlingInput>, {
    id: number,
    dangerousGoods: Array<{ key: string } & DangerousGoodsFieldValue>
}>

// @ts-ignore
const handlingToFormValue: (handling: DeepPartialGraph<Handling>) => HandlingFormValue = ({
    id, terminalName, deleted, loadingUnitIdentifier, checkedIn, checkedOut, checkInDate,
    checkOutDate, area, bay, row, tier, loadingUnitType, loadingUnitSize, loadingUnitSizeFromGate,
    shippingAgencyOrCarrier, ship, unboardingShip, boardingShip, voyage, originVoyage, originLocode,
    destinationVoyage, destinationLocode, type, equipmentType, direct, approvedByTerminal,
    checkedByTerminal, boardingPrescriptions, unboardingPrescriptions, booking,
    bookingFromTerminal, boardingShipFromTerminal, unboardingShipFromTerminal, dangerousGoods
}) => {
    return {
        id: id ?? 0,
        terminalName: terminalName ?? '',
        deleted,
        loadingUnitIdentifier: loadingUnitIdentifier ?? '',
        checkedIn,
        checkedOut,
        checkInDate,
        checkOutDate,
        area,
        bay,
        row,
        tier,
        loadingUnitType,
        loadingUnitSize,
        loadingUnitSizeFromGate,
        shippingAgencyOrCarrier,
        ship,
        unboardingShip,
        boardingShip,
        voyage,
        originVoyage,
        originLocode,
        destinationVoyage,
        destinationLocode,
        type,
        equipmentType,
        direct,
        approvedByTerminal,
        checkedByTerminal,
        boardingPrescriptions,
        unboardingPrescriptions,
        booking,
        bookingFromTerminal,
        boardingShipFromTerminal,
        unboardingShipFromTerminal,
        dangerousGoods: map(dangerousGoods ?? [], dangerousGoodsToFieldValueWithKey)
    }
};

//const handlingTypesWithUnboarding = [HandlingType.Unboarding, HandlingType.TranshipmentBoarding, HandlingType.TranshipmentUnboarding]
//const handlingTypesWithBoarding = [HandlingType.Boarding, HandlingType.TranshipmentBoarding, HandlingType.TranshipmentUnboarding]

type HandlingFormMutationArguments = Override<HacpackMutationCreateOrUpdateHandlingsArguments, { handlings: [DeepPartial<HandlingInput>] }>

const handlingFormMutation = gql<{ createOrUpdateHandlings: HandlingsResult }, HandlingFormMutationArguments>`
    mutation ($handlings: [HandlingInput!]!) {
        createOrUpdateHandlings(handlings: $handlings)  {
            isOk,
            data {
                id
                type
                loadingUnitIdentifier
                checkedIn
                checkedOut
            }
        }
    }
`;

const HandlingForm = ({ handling, onSaved, onCancel, editableFields }: Props) => {

    const [saveResult, save] = useMutation<{ createOrUpdateHandlings: HandlingsResult }, HandlingFormMutationArguments>(handlingFormMutation);
    // @ts-ignore
    const { fetchingApproval: saving, data } = saveResult;

    const result = data?.createOrUpdateHandlings;

    useEffect(() => {
        if (result === undefined || result === null) {
            return;
        }
        const { isOk, data } = result;

        if (isOk === false || data === null) {
            return;
        }

        // @ts-ignore
        onSaved?.(data[0] as DeepPartialGraph<Handling>);
    }, [result, onSaved]);

    const { terminal } = useContext(TerminalContext);

    const defaultValues = useMemo(() => handlingToFormValue(handling ?? { id: 0, terminalName: terminal?.name }), [handling])

    const { id } = useId();
    const { t } = useTranslation('common');

    const form = useForm<HandlingFormValue>({
        defaultValues: defaultValues
    });

    const { register, control, handleSubmit } = form;

    //const watchType = watch('type', handling.type ?? HandlingType.None);

    //const hasUnboarding = includes(handlingTypesWithUnboarding, watchType);
    //const hasBoarding = includes(handlingTypesWithBoarding, watchType);


    const dangerousGoodsFieldArray = useFieldArray<HandlingFormValue, 'dangerousGoods', 'key'>({
        name: 'dangerousGoods',
        keyName: 'key',
        control
    });

    //useEffect(() => {
    //    if (hasUnboarding === false) {
    //        return;
    //    }

    //    return () => {
    //        unregister('unboardingShip');
    //        unregister('originVoyage');
    //    }
    //}, [hasUnboarding])

    //useEffect(() => {
    //    if (hasBoarding === false) {
    //        return;
    //    }

    //    return () => {
    //        unregister('boardingShip');
    //        unregister('destinationVoyage');
    //    }
    //}, [hasBoarding])


    const unitNumberField = {
        id: `loadingUnitIdentifier_${id}`,
        className: 'form-control',
        readOnly: editableFields !== undefined && !includes(editableFields, 'loadingUnitIdentifier'),
        ...register('loadingUnitIdentifier', {
            required: t('Questo campo è obbligatorio').toString()
        })
    };

    const shipFormField = useMemo(() => {

        const shipInputField = {
            id: `ship_${id}`,
            className: 'form-control',
            readOnly: editableFields !== undefined && !includes(editableFields, 'ship'),
            ...register('ship', {
                required: t('Questo campo è obbligatorio').toString()
            })
        };

        return <div style={{ display: 'flex', gap: '.5em', alignItems: 'center', flexShrink: 0 }}>
            <Label style={{ whiteSpace: 'nowrap', margin: 0 }} for={shipInputField.id}>{t('Nave')}</Label>
            <input type="text" {...shipInputField} />
        </div>
    }, [saving, t, editableFields])

    //const unboardingFormField = useMemo(() => {
    //    if (hasUnboarding === false) {
    //        return;
    //    }

    //    const unboardingShipInputField = {
    //        id: `unboardingShip_${id}`,
    //        className: 'form-control',
    //        ...register('unboardingShip', {
    //            required: t('Questo campo è obbligatorio').toString()
    //        })
    //    };

    //    return <div style={{ display: 'flex', gap: '.5em', alignItems: 'center', flexShrink: 0 }}>
    //        <Label style={{ whiteSpace: 'nowrap', margin: 0 }} for={unboardingShipInputField.id}>{t('Nave di sbarco')}</Label>
    //        <input type="text" disabled={saving} {...unboardingShipInputField} />
    //    </div>
    //}, [hasUnboarding, saving, t])

    //const originVoyageFormField = useMemo(() => {
    //    if (hasUnboarding === false) {
    //        return;
    //    }

    //    const originVoyageInputField = {
    //        id: `originVoyage_${id}`,
    //        className: 'form-control',
    //        ...register('originVoyage', {
    //            required: t('Questo campo è obbligatorio').toString()
    //        })
    //    };

    //    return <div style={{ display: 'flex', gap: '.5em', alignItems: 'center', flexShrink: 0 }}>
    //        <Label style={{ whiteSpace: 'nowrap', margin: 0 }} for={originVoyageInputField.id}>{t('Viaggio di origine')}</Label>
    //        <input type="text" disabled={saving} {...originVoyageInputField} />
    //    </div>
    //}, [hasUnboarding, saving, t])

    //const boardingFormField = useMemo(() => {
    //    if (hasBoarding === false) {
    //        return;
    //    }

    //    const boardingShipInputField = {
    //        id: `boardingShip_${id}`,
    //        className: 'form-control',
    //        ...register('boardingShip', {
    //            required: t('Questo campo è obbligatorio').toString()
    //        })
    //    };

    //    return < div style={{ display: 'flex', gap: '.5em', alignItems: 'center', flexShrink: 0 }
    //    }>
    //        <Label style={{ whiteSpace: 'nowrap', margin: 0 }} for={boardingShipInputField.id}>{t('Nave d\'imbarco')}</Label>
    //        <input type="text" disabled={saving} {...boardingShipInputField} />
    //    </div >
    //}, [hasBoarding, saving, t])

    //const destinationVoyageFormField = useMemo(() => {
    //    if (hasBoarding === false) {
    //        return;
    //    }

    //    const destinationVoyageInputField = {
    //        id: `destinationVoyage_${id}`,
    //        className: 'form-control',
    //        ...register('destinationVoyage', {
    //            required: t('Questo campo è obbligatorio').toString()
    //        })
    //    };

    //    return <div style={{ display: 'flex', gap: '.5em', alignItems: 'center', flexShrink: 0 }}>
    //        <Label style={{ whiteSpace: 'nowrap', margin: 0 }} for={destinationVoyageInputField.id}>{t('Viaggio di destinazione')}</Label>
    //        <input type="text" disabled={saving} {...destinationVoyageInputField} />
    //    </div>
    //}, [hasBoarding, saving, t])

    const typeFormField = useMemo(() => {
        const readonly = editableFields !== undefined && !includes(editableFields, 'type');

        // id: `type_${id}`,
        // className={form-control},
        const fieldId = `type_${id}`;

        const typeField = register('type', { required: t('Questo campo è obbligatorio').toString() });

        return <div style={{ display: 'flex', flexWrap: 'nowrap', gap: '.5em', alignItems: 'center', flexShrink: 0 }}>
            <Label style={{ whiteSpace: 'nowrap', margin: 0 }} for={fieldId}>{t('Tipo')}</Label>

            {readonly
                ? <div className="d-none">
                    <input type="hidden" {...typeField} />
                </div> : null}
            <select id={fieldId} className="form-control" {...(readonly ? { value: handling?.type ?? '', disabled: true } : typeField)} style={{ width: 'auto' }}>
                <option value=''>{t('--seleziona tipo movimentazione--')}</option>
                <option value={HandlingType.Unboarding}>{t('Sbarco')}</option>
                <option value={HandlingType.Boarding}>{t('Imbarco')}</option>
                <option value={handling?.type === HandlingType.TranshipmentBoarding ? HandlingType.TranshipmentBoarding : HandlingType.TranshipmentUnboarding}>{t('Transhipment')}</option>
                <option value={HandlingType.Transit}>{t('Transito')}</option>
            </select>
        </div>
    }, [handling, saving, t])

    const sizeFormField = useMemo(() => {
        const registerOptions = {
            required: t('Campo obbligatorio').toString()
        };

        const readonly = editableFields !== undefined && !includes(editableFields, 'loadingUnitSize');

        return <>
            <fieldset disabled={readonly} style={{ display: 'flex', flexWrap: 'nowrap', gap: '.5em', alignItems: 'center', flexShrink: 0 }}>
                <Label style={{ whiteSpace: 'nowrap', margin: 0 }}>{t('Dimensioni')}</Label>

                <div className="form-check form-check-inline">
                    <input className="form-check-input" type="radio" id={`loadingUnitSize_1_${id}`} value="20"  {...register('loadingUnitSize', registerOptions)} />
                    <label className="form-check-label" htmlFor={`loadingUnitSize_1_${id}`}>{t('20 ft')}</label>
                </div>
                <div className="form-check form-check-inline">
                    <input className="form-check-input" type="radio" id={`loadingUnitSize_2_${id}`} value="40" {...register('loadingUnitSize', registerOptions)} />
                    <label className="form-check-label" htmlFor={`loadingUnitSize_2_${id}`}>{t('40 ft')}</label>
                </div>
            </fieldset>
            {readonly === true ? <div className="d-none"><input type="hidden" {...register('loadingUnitSize')} /></div> : null}
        </>
    }, [register, saving, t])

    //#region Save

    const onSubmit: SubmitHandler<HandlingFormValue> = useCallback((handling) => {
        if (handling === undefined) {
            return;
        }

        const { ship, type } = handling;
        const voyage = `${ship}-${id}`;

        const dangerousGoods = map(handling.dangerousGoods.map(({ key, adrSecondaryClasses, ...dg }) => {
            return { ...dg, type, adrSecondaryClasses: adrSecondaryClasses ?? [] };
        }));

        save({ handlings: [{ ...handling, voyage, dangerousGoods: (dangerousGoods as DeepPartial<DangerousGoodsInput>[]) }] });
    }, [save, id]);

    //#endregion

    const header = useMemo(() => {
        const newHandling = handling === undefined || handling.id === 0;
        const title = newHandling ? t('Nuovo previsionale') : t('Modifica dati');


        return <Navbar className="navbar ng-white border-bottom box-shadow" style={{ padding: 0 }} light>
            <HorizontalScroll>
                <div style={{ display: 'flex', flexWrap: 'nowrap', width: 'auto', minWidth: '100%', gap: '1em' }}>
                    <div style={{ flex: 0, display: 'flex', flexWrap: 'nowrap', alignItems: 'center', padding: '4px 0 4px 8px' }}>
                        <span className="text-nowrap" style={{ lineHeight: '38px' }}>
                            <strong>{title}</strong>
                        </span>
                    </div>

                    <div style={{ display: 'flex', flex: 1, flexBasis: 'auto', alignItems: 'center', padding: '4px 0' }}>


                        <div style={{ display: 'flex', flexWrap: 'nowrap', gap: '.5em' }}>

                            {/*<div style={{ display: 'flex', flexWrap: 'nowrap', gap: '.5em', alignItems: 'center', flexShrink: 0 }}>*/}
                            {/*    <Label style={{ whiteSpace: 'nowrap', margin: 0 }} for={shipInputField.id}>{t('Nave')}</Label>*/}
                            {/*    <input type="text" disabled={fetching || showResult} {...shipInputField} />*/}
                            {/*</div>*/}
                            {/*<div style={{ display: 'flex', flexWrap: 'nowrap', gap: '.5em', alignItems: 'center', flexShrink: 0 }}>*/}
                            {/*    <Label style={{ whiteSpace: 'nowrap', margin: 0 }} for={arrivalInputField.id}>{t('Previsto arrivo')}</Label>*/}
                            {/*    <input type="date" disabled={fetching || showResult} {...arrivalInputField} />*/}
                            {/*</div>*/}
                        </div>
                    </div>

                    <div style={{ display: 'flex', flex: 0, flexWrap: 'nowrap', alignItems: 'center', gap: '.5em', padding: '4px 8px 4px 0' }}>
                        {onCancel !== undefined ? <Button type='button' disabled={saving} color="secondary" onClick={onCancel}>{t('Annulla')}</Button> : null}
                        <Button type='submit' disabled={saving} color="primary" style={{ whiteSpace: 'nowrap' }}>{t('Salva')}</Button>
                        {/*<Button type="submit" color="primary" className="form-control" disabled={fetching || showResult || files === undefined}>{t('Salva')}</Button>*/}
                        {/*<Button className="ml-sm-2 form-control" onClick={handleCancelButtonClick} disabled={cancelDisabled}>{t('Annulla')}</Button>*/}
                    </div>
                </div>
            </HorizontalScroll>
        </Navbar>;
    }, [handling, saving, onCancel])

    // @ts-ignore
    const dgFrmTbl = <DangerousGoodsFormTable formId={id} name={'dangerousGoods'} {...dangerousGoodsFieldArray} disabled={editableFields !== undefined && !includes(editableFields, 'dangerousGoods')} />;

    // @ts-ignore
    return <FormProvider {...form} >
        <Form onSubmit={handleSubmit(onSubmit)}>
            <GridLayout header={header}>
                <Wrap>
                    <div>
                        <fieldset style={{ display: 'flex', flexWrap: 'wrap', gap: '1em', justifyContent: 'space-between' }}>
                            {/*<div className='d-none'>*/}
                            {/*    <input type="hidden" {...register('terminalName')} />*/}
                            {/*</div>*/}

                            <div style={{ display: 'flex', flexWrap: 'nowrap', gap: '.5em', alignItems: 'center', flexShrink: 0 }}>
                                <Label style={{ whiteSpace: 'nowrap', margin: 0 }} for={unitNumberField.id}>{t('Sigla')}</Label>
                                <input type="text" {...unitNumberField} />
                            </div>
                            {sizeFormField}

                            {typeFormField}
                            {shipFormField}

                            {/*{unboardingFormField}*/}
                            {/*{originVoyageFormField}*/}

                            {/*{boardingFormField}*/}
                            {/*{destinationVoyageFormField}*/}
                        </fieldset>
                    </div>


                    {dgFrmTbl}

                </Wrap>
            </GridLayout>
        </Form>
    </FormProvider>;
};

export default HandlingForm;

